import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useCan } from '../../stores/session';
import { useDocumentTitle } from '../../lib/title';

type Severity = 'critical' | 'high' | 'low';
type Status = 'open' | 'actioned' | 'dismissed';

interface Flag {
  id: number;
  subject_type: 'chat' | 'comment';
  subject_id: number;
  context_id: number | null;
  severity: Severity;
  category: string;
  confidence: number;
  excerpt: string;
  reason: string;
  auto_hidden: boolean;
  status: Status;
  created_at: string;
  handled_at: string | null;
  handled_by: string | null;
  note: string | null;
  author: { id: number; username: string; display_name: string } | null;
  /** How many times this author has come up in the last thirty days. */
  author_flags: number;
}

interface Offender {
  id: number;
  username: string;
  display_name: string;
  flags: number;
  critical: number;
  high: number;
  latest: string;
}

interface Payload {
  /** Whether anything is actually being classified. See the note below. */
  enabled: boolean;
  counts: { critical: number; high: number; low: number; open: number };
  items: Flag[];
  repeat_offenders: Offender[];
}

const TABS: { id: Status; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'actioned', label: 'Actioned' },
  { id: 'dismissed', label: 'Dismissed' },
];

const SEVERITY: Record<Severity, { label: string; className: string }> = {
  critical: { label: 'critical', className: 'border-hot-edge bg-hot-wash text-hot-text' },
  high: { label: 'high', className: 'border-line-strong bg-raised text-ink' },
  low: { label: 'low', className: 'border-line text-faint' },
};

/**
 * What the classifier flagged, and who keeps coming up.
 *
 * ## Two lists, and the second one is why the quiet tier exists
 *
 * The flags are the obvious half. The accounts above them are the half that
 * reporting cannot do: one sharp remark in an argument is nothing, and the same
 * person appearing forty times across six rooms in a week is somebody worth
 * looking at - and not one of those forty messages would ever have been
 * reported. That pattern is the whole reason low-severity verdicts are stored at
 * all, and it is the first thing on the page rather than a tab nobody opens.
 *
 * ## Dismissing is a first-class action, not a way to clear a row
 *
 * This reads a music site, where people quote lyrics containing exactly the
 * language it is looking for. Some proportion of what lands here will be a
 * quotation or an argument about a mix, and `Dismiss` is how that gets recorded
 * - which is what turns the false-positive rate into a number somebody can look
 * at instead of a feeling they have.
 *
 * ## It says when it is switched off
 *
 * An empty screen otherwise means either "nothing has been flagged" or "there is
 * no API key on this box and nothing is being read at all", and those are
 * opposite facts. The banner is the difference between good news and a feature
 * silently doing nothing.
 */
export function AdminSafetyPage() {
  useDocumentTitle('Safety');

  const canAction = useCan('safety.action');
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>('open');

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'safety', status],
    queryFn: () => api.get<Payload>(`/admin/safety?status=${status}`),
    // Short, because this is the screen somebody keeps open when they are
    // worried about a room.
    refetchInterval: 60_000,
  });

  const resolve = useMutation({
    mutationFn: ({ id, next, note }: { id: number; next: Exclude<Status, 'open'>; note?: string }) =>
      api.post(`/admin/safety/${id}`, { status: next, note }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admin', 'safety'] }),
  });

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Safety</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-soft">
          Room chat and track comments, read automatically. Critical flags are taken down and
          emailed out; everything else waits here. Nothing in this list has been reported by a
          person — that queue is under{' '}
          <Link to="/admin/reports" className="text-accent hover:underline">
            Reports
          </Link>
          .
        </p>
      </header>

      {data && !data.enabled && (
        <Alert tone="info">
          No classifier is configured on this server, so nothing is being read. Set{' '}
          <code className="font-mono text-[13px]">MODERATION_API_KEY</code> and restart the safety
          worker. Anything below is from a time when it was running.
        </Alert>
      )}

      {data && data.repeat_offenders.length > 0 && (
        <Card className="flex flex-col gap-3 p-5">
          <div className="flex flex-col gap-1">
            <h2 className="meta-caps text-faint">Keeps coming up</h2>
            <p className="text-xs text-soft">
              Three or more flags in the last thirty days. No single one of these is necessarily
              worth acting on — the pattern might be.
            </p>
          </div>

          <ul className="flex flex-col gap-1.5">
            {data.repeat_offenders.map((person) => (
              <li key={person.id}>
                <Link
                  to={`/admin/users/${person.id}`}
                  className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-[var(--radius-control)] border border-line px-3 py-2 transition-colors hover:border-line-strong hover:bg-raised"
                >
                  <span className="text-sm font-medium text-ink">
                    {person.display_name}{' '}
                    <span className="font-mono text-[11px] text-faint">@{person.username}</span>
                  </span>
                  <span className="font-mono text-[11px] text-soft">
                    {person.flags} flags
                    {person.critical > 0 && (
                      <span className="text-hot-text"> · {person.critical} critical</span>
                    )}
                    {person.high > 0 && <span> · {person.high} high</span>}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            data-safety-tab={tab.id}
            onClick={() => setStatus(tab.id)}
            className={`rounded-[var(--radius-pill)] border px-4 py-2 text-sm transition-colors ${
              status === tab.id
                ? 'border-line-strong bg-raised font-medium text-ink'
                : 'border-line text-soft hover:text-ink'
            }`}
          >
            {tab.label}
            {tab.id === 'open' && data && data.counts.open > 0 && (
              <span className="ml-2 font-mono text-[11px] text-hot-text">{data.counts.open}</span>
            )}
          </button>
        ))}
      </div>

      {isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : (data?.items.length ?? 0) === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm text-soft">
            {status === 'open'
              ? 'Nothing waiting. Quiet is the normal state here.'
              : 'Nothing in this list.'}
          </p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-3" data-safety-flags>
          {data?.items.map((flag) => (
            <FlagCard
              key={flag.id}
              flag={flag}
              canAction={canAction}
              busy={resolve.isPending}
              onResolve={(next, note) => resolve.mutate({ id: flag.id, next, note })}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function FlagCard({
  flag,
  canAction,
  busy,
  onResolve,
}: {
  flag: Flag;
  canAction: boolean;
  busy: boolean;
  onResolve: (next: 'actioned' | 'dismissed', note?: string) => void;
}) {
  const [note, setNote] = useState('');
  const tone = SEVERITY[flag.severity];

  return (
    <li>
      <Card className="flex flex-col gap-4 p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`meta-caps rounded-[var(--radius-chip)] border px-2.5 py-1 ${tone.className}`}
          >
            {tone.label}
          </span>
          <span className="font-mono text-[11px] text-soft">
            {flag.category.replace(/_/g, ' ')} · {flag.confidence}% · {flag.subject_type}
          </span>
          {flag.auto_hidden && (
            <span className="meta-caps rounded-[var(--radius-chip)] border border-line px-2 py-1 text-faint">
              taken down
            </span>
          )}
          <span className="ml-auto font-mono text-[10px] text-ghost">
            {new Date(flag.created_at).toLocaleString()}
          </span>
        </div>

        {/*
          The classifier's reasoning above the message it is about.

          Deliberately in that order: a moderator reading the words first forms
          a view and then reads a sentence agreeing with it, which is how a
          machine's mistake becomes a person's. The claim first, the evidence
          second, is the order in which the claim can actually be checked.
        */}
        <p className="text-sm leading-relaxed text-ink">{flag.reason}</p>

        <blockquote className="border-l-2 border-line-strong pl-4 text-[13px] leading-relaxed text-soft">
          {/*
            The snapshot, not the live row. It is kept because the message is
            usually gone by the time anybody reads this - taken down
            automatically, or by the host within seconds - and a flag nobody can
            check is a flag nobody should act on.
          */}
          {flag.excerpt}
        </blockquote>

        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          {flag.author ? (
            <Link
              to={`/admin/users/${flag.author.id}`}
              className="font-mono text-soft hover:text-ink hover:underline"
            >
              @{flag.author.username}
            </Link>
          ) : (
            <span className="font-mono text-faint">account removed</span>
          )}

          {/* The count that answers "bad minute or bad account" before the
              decision rather than after it. */}
          {flag.author_flags > 1 && (
            <span className="font-mono text-faint">
              {flag.author_flags} flags in 30 days
            </span>
          )}

          {flag.subject_type === 'chat' && flag.context_id !== null && (
            <span className="font-mono text-ghost">room #{flag.context_id}</span>
          )}
          {flag.subject_type === 'comment' && flag.context_id !== null && (
            <Link
              to={`/tracks/${flag.context_id}`}
              className="font-mono text-soft hover:text-ink hover:underline"
            >
              on the track →
            </Link>
          )}
        </div>

        {flag.status === 'open' && canAction && (
          <div className="flex flex-wrap items-center gap-2 border-t border-line pt-4">
            <input
              value={note}
              maxLength={500}
              placeholder="note (optional)"
              onChange={(event) => setNote(event.target.value)}
              className="min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-raised px-3 py-2 text-[13px] text-ink outline-none placeholder:text-faint focus:border-line-lit"
            />
            <Button
              variant="secondary"
              disabled={busy}
              data-dismiss-flag={flag.id}
              onClick={() => onResolve('dismissed', note || undefined)}
            >
              Not a problem
            </Button>
            <Button
              disabled={busy}
              data-action-flag={flag.id}
              onClick={() => onResolve('actioned', note || undefined)}
            >
              Dealt with
            </Button>
          </div>
        )}

        {flag.status !== 'open' && (
          <p className="border-t border-line pt-3 font-mono text-[11px] text-faint">
            {flag.status === 'dismissed' ? 'Dismissed' : 'Actioned'}
            {flag.handled_by !== null && ` by @${flag.handled_by}`}
            {flag.note !== null && ` — ${flag.note}`}
          </p>
        )}
      </Card>
    </li>
  );
}
